/**
 * CheckpointTimelineModule — admin Checkpoint Timeline (ODE-P07-T04).
 *
 * Renders one delivery order's CP1..CP7 timeline (REQ-045) with the REQ-012
 * sequential slot states (`completed` / `locked` / `pending`), the REQ-031
 * server-recorded timestamp + captured GPS per accepted checkpoint, and the
 * REQ-076 actor/source/audit-event linkage — all taken from the server
 * projection verbatim (the client never computes a state, a GPS fix or an
 * audit link).
 *
 * REQ-090 states rendered: idle (no DO selected), loading, ready, empty,
 * not_found, unauthorized, blocked, error.
 *
 * Accessibility: every slot carries a stable `data-testid`, status is never
 * encoded by colour alone (the state token is textual), and the region
 * announces updates politely.
 *
 * Traces to: ODE-PRD-REQ-045, ODE-PRD-REQ-012, ODE-PRD-REQ-031,
 *            ODE-PRD-REQ-073, ODE-PRD-REQ-076, ODE-PRD-REQ-090.
 */

import { useAuth } from '../../auth/AuthContext.js';
import type { CheckpointTimeline, CheckpointTimelineSlot } from '../../api/client.js';
import { StateView } from '../../components/StateView.js';
import type { ModuleState } from '../state.js';
import {
  useCheckpointTimeline,
  type CheckpointTimelineState,
  type UseCheckpointTimelineOptions,
} from './useCheckpointTimeline.js';

/** Plain-Indonesian labels for the three REQ-012 slot states. */
export const SLOT_STATE_LABELS: Readonly<Record<CheckpointTimelineSlot['state'], string>> = {
  completed: 'Selesai',
  locked: 'Terkunci',
  pending: 'Menunggu',
};

/** Non-colour status tokens (never rely on colour alone). */
export const SLOT_STATE_TOKENS: Readonly<Record<CheckpointTimelineSlot['state'], string>> = {
  completed: '[SELESAI]',
  locked: '[TERKUNCI]',
  pending: '[MENUNGGU]',
};

/** Plain-Indonesian labels for the bounded blocking reasons. */
export const BLOCKING_LABELS: Readonly<Record<string, string>> = {
  PREVIOUS_CHECKPOINT_INCOMPLETE: 'Menunggu checkpoint sebelumnya selesai',
  DO_TERMINAL: 'Delivery Order sudah terminal — checkpoint terkunci',
};

export interface CheckpointTimelineSeams {
  readonly fetch?: UseCheckpointTimelineOptions['fetch'];
}

function formatGps(slot: CheckpointTimelineSlot): string {
  if (slot.gps === null) return 'GPS belum tercatat';
  const accuracy = slot.gps.accuracyMeters === null ? '' : ` (±${slot.gps.accuracyMeters} m)`;
  return `${slot.gps.latitude}, ${slot.gps.longitude}${accuracy}`;
}

function slotDetail(slot: CheckpointTimelineSlot): string {
  if (slot.state === 'completed') {
    // REQ-073: only a truly accepted COMPLETED slot is server-authoritative; a
    // CP7 COMPLETION_PENDING slot is done operationally but not validated.
    const authority = slot.authoritative
      ? 'divalidasi server'
      : 'menunggu validasi server (REQ-073)';
    return `${slot.occurredAt ?? 'waktu tidak tercatat'} · ${formatGps(slot)} · ${authority}`;
  }
  if (slot.state === 'locked') {
    const reason =
      slot.blockingReason === null
        ? 'terkunci'
        : (BLOCKING_LABELS[slot.blockingReason] ?? slot.blockingReason);
    return slot.blockingCheckpoint === null ? reason : `${reason} (${slot.blockingCheckpoint})`;
  }
  return 'Belum dimulai — tersedia untuk dijalankan driver.';
}

function SlotRow({ slot }: { readonly slot: CheckpointTimelineSlot }): JSX.Element {
  const label = SLOT_STATE_LABELS[slot.state];
  return (
    <li
      className={`cp-slot cp-slot-${slot.state}`}
      data-testid={`cp-slot-${slot.checkpoint}`}
      aria-label={`${slot.checkpoint} ${slot.name}: ${label}`}
    >
      <div className="cp-slot-head">
        <span className="cp-slot-id" data-testid={`cp-slot-id-${slot.checkpoint}`}>
          {slot.checkpoint}
        </span>
        <span className="cp-slot-name">{slot.name}</span>
        <span
          className={`cp-slot-state cp-slot-state-${slot.state}`}
          data-testid={`cp-state-${slot.checkpoint}`}
        >
          {SLOT_STATE_TOKENS[slot.state]} {label}
        </span>
      </div>
      <p className="cp-slot-detail" data-testid={`cp-detail-${slot.checkpoint}`}>
        {slotDetail(slot)}
      </p>
      <dl className="cp-slot-meta">
        <dt>Waktu (server)</dt>
        <dd data-testid={`cp-time-${slot.checkpoint}`}>{slot.occurredAt ?? '—'}</dd>
        <dt>GPS</dt>
        <dd data-testid={`cp-gps-${slot.checkpoint}`}>{formatGps(slot)}</dd>
        <dt>Aktor</dt>
        <dd data-testid={`cp-actor-${slot.checkpoint}`}>
          {slot.actor === null ? '—' : `${slot.actor.type}:${slot.actor.id}`}
        </dd>
        <dt>Audit (REQ-076)</dt>
        <dd data-testid={`cp-audit-${slot.checkpoint}`}>{slot.auditEventId ?? '—'}</dd>
      </dl>
    </li>
  );
}

function TimelineView({ timeline }: { readonly timeline: CheckpointTimeline }): JSX.Element {
  return (
    <div className="cp-timeline" data-testid="cp-timeline">
      <p className="cp-timeline-summary" data-testid="cp-timeline-summary">
        {timeline.summary.completed} dari {timeline.summary.total} checkpoint selesai ·{' '}
        {timeline.summary.locked} terkunci · {timeline.summary.pending} menunggu
      </p>
      <p className="cp-timeline-source" data-testid="cp-timeline-source">
        Sumber: {timeline.source} (otoritatif server; keputusan perangkat tidak otoritatif)
      </p>
      <ol className="cp-slot-list">
        {timeline.checkpoints.map((slot) => (
          <SlotRow key={slot.checkpoint} slot={slot} />
        ))}
      </ol>
    </div>
  );
}

/** Maps the module state to the shared REQ-090 StateView state. */
export function toModuleState(state: CheckpointTimelineState): ModuleState {
  switch (state.kind) {
    case 'idle':
      return { kind: 'pending' };
    case 'loading':
      return { kind: 'loading' };
    case 'empty':
      return { kind: 'empty', payload: null };
    case 'not_found':
      // Handled by a dedicated inline alert (below) — the REQ-090 set has no
      // distinct not-found state, so this mapping is only a fallback.
      return { kind: 'empty', payload: null };
    case 'unauthorized':
      return { kind: 'unauthorized' };
    case 'blocked':
      return { kind: 'blocked', message: state.message };
    case 'error':
      return { kind: 'error', message: state.message };
    case 'ready':
      return { kind: 'pending' };
  }
}

export function CheckpointTimelineModule({
  seams = {},
}: { readonly seams?: CheckpointTimelineSeams } = {}): JSX.Element {
  const { session } = useAuth();
  const timeline = useCheckpointTimeline({
    token: session?.token ?? null,
    ...(seams.fetch === undefined ? {} : { fetch: seams.fetch }),
  });
  const { state } = timeline;

  return (
    <section aria-labelledby="checkpoint-timeline-title" className="module">
      <h2 id="checkpoint-timeline-title">Linimasa Checkpoint</h2>
      <p className="module-description">
        Lihat urutan CP1–CP7 untuk satu Delivery Order: status tiap checkpoint
        (REQ-012), waktu &amp; GPS yang tercatat server (REQ-031), serta aktor dan
        tautan audit (REQ-076).
      </p>

      <form
        className="cp-timeline-form"
        data-testid="cp-timeline-form"
        onSubmit={(event) => {
          event.preventDefault();
          timeline.submit();
        }}
      >
        <label>
          ID Delivery Order
          <input
            data-testid="cp-do-id"
            value={timeline.doId}
            onChange={(event) => timeline.setDoId(event.target.value)}
            placeholder="mis. 55555555-5555-4555-8555-555555555555"
          />
        </label>
        <button type="submit" data-testid="cp-timeline-submit">
          Tampilkan linimasa
        </button>
      </form>

      {state.kind === 'ready' && <TimelineView timeline={state.timeline} />}
      {state.kind === 'not_found' && (
        <section
          role="alert"
          className="state state-error"
          data-testid="cp-not-found"
        >
          <h3>Delivery Order tidak ditemukan</h3>
          <p>Tidak ada checkpoint untuk ID {state.doId}.</p>
        </section>
      )}
      {state.kind !== 'ready' && state.kind !== 'not_found' && (
        <StateView state={toModuleState(state)} onRetry={() => timeline.submit()} />
      )}
    </section>
  );
}
